import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/card/Card';
import Spinner from '../../components/Spinner/spinner';
import { getMonetizationRequestContext } from '../../services/GuaranteesService';
import { routes } from '../../routes/routePaths';
import { buildPrefilledMonetizationSearchParams } from '../../utils/monetizationPrefilledNavigation';
import CuitSearchStep from './CuitSearchStep';
import ProductSelectionStep from './ProductSelectionStep';
import type { 
  WizardState, 
  MonetizationRequestSearchResponse, 
  MonetizationRequestProduct,
  MonetizationRequestContextResponse
} from './types';

const initialState: WizardState = {
  currentStep: 1,
  cuit: '',
  searchResult: null,
  selectedProduct: null,
  context: null,
  error: null,
};

const NewRequestWizard = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<WizardState>(initialState);
  const [isLoadingContext, setIsLoadingContext] = useState(false);

  const setStep = useCallback((step: number) => {
    setState(prev => ({ ...prev, currentStep: step, error: null }));
  }, []);

  const setCuit = useCallback((cuit: string) => {
    setState(prev => ({ ...prev, cuit }));
  }, []);

  const setSearchResult = useCallback((result: MonetizationRequestSearchResponse | null) => {
    setState(prev => ({ ...prev, searchResult: result }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const handleSearchSuccess = useCallback((result: MonetizationRequestSearchResponse) => {
    setSearchResult(result);
    setError(null);
    setStep(2);
  }, [setSearchResult, setError, setStep]);

  const handleProductSelect = useCallback(async (product: MonetizationRequestProduct) => {
    if (product.availableOperable <= 0) return;

    setState(prev => ({ ...prev, selectedProduct: product }));
    setIsLoadingContext(true);
    setError(null);

    try {
      const context: MonetizationRequestContextResponse = await getMonetizationRequestContext(
        product.cuit,
        product.productId,
        product.currencyId
      );

      setState(prev => ({ ...prev, context }));

      // Navigate to the prefilled monetization form
      const params = buildPrefilledMonetizationSearchParams(context);

      navigate(`${routes.newRequestLoad}?${params.toString()}`);
    } catch (error: any) {
      console.error('Error getting monetization context:', error);
      setError('Error al obtener la información del producto. Por favor, intentá nuevamente.');
    } finally {
      setIsLoadingContext(false);
    }
  }, [navigate, setError]);

  const handleReset = useCallback(() => {
    setState(initialState);
  }, []);

  const renderStepContent = () => {
    if (isLoadingContext) {
      return (
        <Card 
          display='flex' 
          flexDirection='column' 
          alignItems='center' 
          justifyContent='center'
          height='300px'
        >
          <Spinner size='lg' variant='primary' />
          <Card margin={{ top: '3' }}>
            Cargando información del producto...
          </Card>
        </Card>
      );
    }

    switch (state.currentStep) {
      case 1:
        return (
          <CuitSearchStep
            cuit={state.cuit}
            onCuitChange={setCuit}
            onSearchSuccess={handleSearchSuccess}
            onError={setError}
          />
        );
      case 2:
        if (!state.searchResult) {
          setStep(1);
          return null;
        }
        return (
          <ProductSelectionStep
            searchResult={state.searchResult}
            onProductSelect={(product) => void handleProductSelect(product)}
            onNewSearch={handleReset}
            cuit={state.cuit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Card width='100%'>
      <Card margin={{ top: '2' }}>
        {renderStepContent()}
      </Card>
    </Card>
  );
};

export default NewRequestWizard;
